import Feedback from '@/models/read/Feedback';
import Document from '@/models/read/Document';
import Editor from '@/models/read/User/Editor';
import QualityChecker from '@/models/read/User/QualityChecker';
import ReferenceSpecialist from '@/models/read/User/ReferenceSpecialist';
import LayoutSpecialist from '@/models/read/User/LayoutSpecialist';

class Assignment {
    constructor({
        id,
        previousAssignmentId,
        services,
        feedback,
        status,
        editor,
        name,
        studentDocument,
        pages,
        words,
        reviewScore,
        deadline,
        type,
        scope,
        startDate,
        canBeThanked: canGiveThankYou,
    }, order) {
        this._id = String(id);
        this._order = order;
        this._previousAssignmentId = previousAssignmentId;
        this._services = services;
        this._feedback = feedback ? new Feedback(feedback) : null;
        this._status = status;
        this._name = name;
        this._thankYou = null;
        this._pages = pages;
        this._words = words;
        this._studentDocument = this._formatStudentDocument(studentDocument);
        this._reviewScore = reviewScore;
        this._deadline = new Date(deadline);
        this._type = type.split('_')[0];
        this._scope = scope;
        this._canGiveThankYou = canGiveThankYou;
        this._startDate = new Date(startDate);
        this._order = order;
        this._startDate = startDate;
        this._editor = this._getEditor(editor);
    }

    get id() {
        return this._id;
    }

    get type() {
        return this._type;
    }

    get order() {
        return this._order;
    }

    get services() {
        return this._services;
    }

    get feedback() {
        return this._feedback;
    }

    get editor() {
        return this._editor;
    }

    get name() {
        return this._name;
    }

    get status() {
        return this._status;
    }

    get hasLanguageProblems() {
        return this._feedback.languageProblems !== null;
    }

    get hasStructureProblems() {
        return this._feedback.structureProblems !== null;
    }

    get hasClarityCheck() {
        return this._feedback.clarityCheck !== null;
    }

    get hasStructureCheck() {
        return this._feedback.structureCheck !== null;
    }

    get isDelivered() {
        return this._status === 'delivered';
    }

    get isCompleted() {
        return this._status === 'completed';
    }

    get isInProgress() {
        return this._status === 'accepted' && (!this._previousAssignmentId || this.previousAssignment.isCompletedOrHigher);
    }

    get startDate() {
        return this._startDate;
    }

    set startDate(value) {
        this._startDate = value;
    }

    get isCompletedOrHigher() {
        return this.isDelivered || this.isCompleted;
    }

    get hasFeedback() {
        if (!this.feedback) {
            return false;
        }
        return !!this.feedback.letter;
    }

    set thankYou(thankYou) {
        this._thankYou = thankYou;
    }

    get thankYou() {
        return this._thankYou;
    }
    get studentDocument() {
        return this._studentDocument;
    }
    get pages() {
        return this._pages;
    }
    get words() {
        return this._words;
    }
    get reviewScore() {
        return this._reviewScore;
    }

    get deadline() {
        return this._deadline;
    }

    get scope() {
        return this._scope;
    }

    get isSampleEdit() {
        return this._type === 'sampleEdit';
    }

    get isEditingService() {
        return this._type === 'editingService';
    }

    get isBundle() {
        return this._type === 'bundle';
    }

    get isLayoutCheck() {
        return this._type === 'layoutCheck';
    }

    get isReferenceCheck() {
        return this._type === 'referenceCheck';
    }

    get isQualityCheck() {
        return this._type === 'qualityCheck';
    }

    _formatStudentDocument(studentDocument) {
        if (!studentDocument) {
            return null;
        }

        studentDocument.selectedPages = this._pages;
        studentDocument.selectedWords = this._words;
        return new Document(studentDocument);
    }

    get canGiveThankYou() {
        return this._canGiveThankYou;
    }

    get previousAssignmentId() {
        return this._previousAssignmentId;
    }

    get previousAssignment() {
        return this._order.assignments.find(a => a.id === this._previousAssignmentId);
    }

    get hasPreviousAssignment() {
        return !!this._previousAssignmentId;
    }

    get hasNextAssignment() {
        return this.nextAssignments.length > 0;
    }

    get nextAssignments() {
        return this._order.assignments.filter(a => String(a.previousAssignmentId) === String(this.id));
    }

    _getEditor(editor) {
        if (!editor) {
            return null;
        }

        if (this.isQualityCheck) {
            return new QualityChecker(editor);
        }

        if (this.isReferenceCheck) {
            return new ReferenceSpecialist(editor);
        }

        if (this.isLayoutCheck) {
            return new LayoutSpecialist(editor);
        }

        return new Editor(editor);
    }
}

export default Assignment;
